import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Play, Square, RefreshCw, AlertTriangle, CheckCircle, Calendar, Phone, Bell, Users, Eye } from 'lucide-react';
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


/**
 * SchedulerManager - Interface de gestion du CRON Scheduler WhatsApp
 * Permet de visualiser et contrôler les jobs automatisés
 */
export default function SchedulerManager() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [nrpStatus, setNrpStatus] = useState(null);
  const [activityStats, setActivityStats] = useState(null);
  const [sheetSyncStatus, setSheetSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // Charger le statut
  const loadStatus = useCallback(async () => {
    try {
      const [statusRes, logsRes, nrpRes, activityRes] = await Promise.all([
        authFetch(`${API}/api/scheduler/status`),
        authFetch(`${API}/api/scheduler/logs?limit=20`),
        authFetch(`${API}/api/scheduler/nrp/status`),
        authFetch(`${API}/api/scheduler/activity/status`),
      ]);
      
      setStatus(await statusRes.json());
      setLogs((await logsRes.json()).logs || []);
      setNrpStatus(await nrpRes.json());
      setActivityStats(await activityRes.json());
    } catch (e) {
      console.error("Erreur chargement scheduler:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    // Refresh toutes les 30 secondes
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, [loadStatus]);

  // Démarrer/Arrêter le scheduler
  const toggleScheduler = async () => {
    setActionLoading('toggle');
    try {
      const endpoint = status?.running ? 'stop' : 'start';
      await authFetch(`${API}/api/scheduler/${endpoint}`, { method: 'POST' });
      await loadStatus();
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(null);
    }
  };

  // Exécuter un job manuellement
  const runJob = async (jobId) => {
    setActionLoading(jobId);
    try {
      const res = await authFetch(`${API}/api/scheduler/run/${jobId}`, { method: 'POST' });
      const result = await res.json();
      console.log("Job result:", result);
      await loadStatus();
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(null);
    }
  };

  // Icône pour chaque type de job
  const getJobIcon = (jobId) => {
    if (jobId.includes('rappels')) return <Calendar className="w-4 h-4" />;
    if (jobId.includes('nrp')) return <Phone className="w-4 h-4" />;
    if (jobId.includes('retour')) return <Users className="w-4 h-4" />;
    if (jobId.includes('activity')) return <Eye className="w-4 h-4" />;
    return <Clock className="w-4 h-4" />;
  };

  // Formater la date
  const formatDate = (isoDate) => {
    if (!isoDate) return '-';
    const d = new Date(isoDate);
    return d.toLocaleString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-slate-400">
        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
        Chargement du scheduler...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Clock className="w-6 h-6 text-purple-400" />
            Scheduler WhatsApp CRON
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Automatisation des rappels J-2/J-1, séquences NRP, et suivis techniciens
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Status indicator */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
            status?.running 
              ? 'bg-green-500/20 text-green-400' 
              : 'bg-red-500/20 text-red-400'
          }`}>
            {status?.running ? (
              <>
                <CheckCircle className="w-4 h-4" />
                Actif
              </>
            ) : (
              <>
                <AlertTriangle className="w-4 h-4" />
                Arrêté
              </>
            )}
          </div>
          
          {/* Toggle button */}
          <button
            onClick={toggleScheduler}
            disabled={actionLoading === 'toggle'}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              status?.running
                ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400'
                : 'bg-green-500/20 hover:bg-green-500/30 text-green-400'
            }`}
            data-testid="scheduler-toggle-btn"
          >
            {actionLoading === 'toggle' ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : status?.running ? (
              <Square className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            {status?.running ? 'Arrêter' : 'Démarrer'}
          </button>
          
          {/* Refresh */}
          <button
            onClick={loadStatus}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700"
            title="Rafraîchir"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Jobs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {status?.jobs?.map((job) => (
          <div 
            key={job.id}
            className="bg-slate-800/50 border border-slate-700 rounded-xl p-4"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-purple-500/20 rounded-lg text-purple-400">
                  {getJobIcon(job.id)}
                </div>
                <div>
                  <h3 className="font-medium text-white text-sm">{job.name}</h3>
                  <p className="text-xs text-slate-500">{job.id}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-400">
                <span className="text-slate-500">Prochaine exécution:</span>
                <br />
                <span className="text-white font-medium">{formatDate(job.next_run)}</span>
              </div>
              
              <button
                onClick={() => runJob(job.id)}
                disabled={actionLoading === job.id}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg text-xs font-medium transition-all"
                data-testid={`run-job-${job.id}`}
              >
                {actionLoading === job.id ? (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                ) : (
                  <Play className="w-3 h-3" />
                )}
                Exécuter
              </button>
            </div>
          </div>
        ))}
      </div>


      {/* NRP Sequences Status */}
      {nrpStatus?.active_sequences > 0 && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
          <h3 className="font-medium text-orange-400 flex items-center gap-2 mb-3">
            <Phone className="w-5 h-5" />
            Séquences NRP actives ({nrpStatus.active_sequences})
          </h3>
          <div className="space-y-2">
            {nrpStatus.sequences?.map((seq) => (
              <div key={seq.row_number} className="flex items-center justify-between bg-slate-800/50 rounded-lg px-3 py-2">
                <div>
                  <span className="text-white font-medium">{seq.nom}</span>
                  <span className="text-slate-400 text-sm ml-2">{seq.telephone}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400">
                    Étape {seq.current_step}/5
                  </span>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((step) => (
                      <div
                        key={step}
                        className={`w-2 h-2 rounded-full ${
                          step <= seq.current_step ? 'bg-orange-400' : 'bg-slate-600'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monitoring Activité Stats */}
      <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4">
        <h3 className="font-medium text-cyan-400 flex items-center gap-2 mb-3">
          <Eye className="w-5 h-5" />
          Monitoring Activité - Aujourd'hui
        </h3>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-white">{activityStats?.nudges_level_1 || 0}</div>
            <div className="text-xs text-slate-400">Nudges envoyés</div>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-red-400">{activityStats?.nudges_level_2 || 0}</div>
            <div className="text-xs text-slate-400">Escalades</div>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-cyan-400">{activityStats?.total_nudges || 0}</div>
            <div className="text-xs text-slate-400">Total</div>
          </div>
        </div>
        {activityStats?.by_telepro?.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-slate-400 mb-2">Par télépro :</p>
            {activityStats.by_telepro.map((t, idx) => (
              <div key={idx} className="flex items-center justify-between bg-slate-800/50 rounded-lg px-3 py-2 text-sm">
                <span className="text-white">{t._id}</span>
                <div className="flex items-center gap-3">
                  <span className="text-cyan-400">{t.count} nudges</span>
                  <span className="text-slate-400 text-xs">max {t.max_inactivity} min</span>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-slate-500 mt-3">
          ⏰ Surveillance 9h-18h | Seuil: 20 min inactivité | Escalade: +30 min
        </p>
      </div>

      {/* Recent Logs */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
        <h3 className="font-medium text-white flex items-center gap-2 mb-4">
          <Bell className="w-5 h-5 text-blue-400" />
          Dernières actions ({logs.length})
        </h3>
        
        {logs.length === 0 ? (
          <p className="text-slate-400 text-sm text-center py-4">
            Aucune action récente
          </p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {logs.map((log, idx) => (
              <div key={idx} className="flex items-center justify-between bg-slate-900/50 rounded-lg px-3 py-2 text-sm">
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full ${
                    log.result?.success ? 'bg-green-400' : 'bg-red-400'
                  }`} />
                  <span className="text-white">{log.type?.replace('scheduler_', '')}</span>
                  <span className="text-slate-400">{log.lead_nom}</span>
                </div>
                <span className="text-xs text-slate-500">{formatDate(log.executed_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
        <h4 className="font-medium text-purple-400 mb-2">📋 Jobs configurés</h4>
        <ul className="text-sm text-slate-300 space-y-1">
          <li>• <strong>Rappels J-2</strong> : 9h00 - Notification aux télépros pour confirmer les RDV dans 2 jours</li>
          <li>• <strong>Rappels J-1</strong> : 9h00 & 14h00 - URGENT pour RDV non confirmés</li>
          <li>• <strong>Séquences NRP</strong> : Toutes les heures (9h-18h) - Relances automatiques NRP #1 à #5</li>
          <li>• <strong>Rappels avant RDV</strong> : Toutes les 15 min - Notification 15 min avant un rappel planifié</li>
          <li>• <strong>Retour technicien</strong> : Toutes les 30 min - Demande feedback H+2 après RDV</li>
          <li>• <strong>Actions planifiées</strong> : Toutes les 5 min - Traitement des actions différées</li>
          <li>• <strong className="text-cyan-400">Monitoring activité</strong> : Toutes les 10 min - Détection inactivité (+20 min) et nudges WhatsApp</li>
        </ul>
      </div>
    </div>
  );
}
